import type {ScanContext, SharedJobData} from "../scan-model/ScanContext.tsx";
import {
    getSharedJobImplementationId
} from "../../../components/scanner/shared-job-scanner/SharedJobImplementationId.ts";
import {runPromises} from "../../../util/promises/PromiseRunner.tsx";
import type {SharedJobScanResult, SingleSharedJobScanResult} from "../scan-model/SharedJobResult.ts";
import {blankProblem, mergeProblemResults} from "./job/mergeProblems.tsx";
import {scanJobDetails} from "./job/scanJobDetails.ts";
import type {OverallProblemIndicators} from "../scan-model/ScanResult.tsx";
import type {MetlSharedJobExport} from "../../../metl-models/exports/MetlSharedJobExport.ts";

function pruneMetadata(metadata: MetlSharedJobExport["metadata"]): MetlSharedJobExport["metadata"] {
    const newMeta = { ...metadata }
    delete newMeta.helpHtml
    delete newMeta.imageMetadata
    newMeta.rootJobReference.parameterMetadata.forEach((param) => {
        delete param.defaultValue
    })

    return newMeta
}

async function scan(ctx: ScanContext, revision: SharedJobData): Promise<SingleSharedJobScanResult | undefined> {
    const exported = await ctx.api
        .sharedJobs.get(revision.packageName)
        .jobs.get(revision.jobName)
        .revisions.export(revision.revision)
    if(exported.objects.length !== 1) return undefined
    const sharedJobExport = exported.objects[0]
    const problemIndicators = blankProblem()
    const componentCount: Record<number, number> = {}
    const metadata = pruneMetadata(sharedJobExport.metadata)

    const jobs = [
        ...Object.entries(sharedJobExport.orchestrationJobs).map(([jobName, job]) => scanJobDetails(ctx, job, "ORCHESTRATION", jobName)),
        ...Object.entries(sharedJobExport.transformationJobs).map(([jobName, job]) => scanJobDetails(ctx, job, "TRANSFORMATION", jobName)),
    ]
    mergeProblemResults(jobs, {problemIndicators, componentCount})
    return {
        jobs,
        problemIndicators,
        metadata,
        componentCount,
    }
}

export async function scanSharedJobs(ctx: ScanContext): Promise<SharedJobScanResult> {
    ctx.log("Retrieving shared job list")
    try {
        const sharedJobPackages = await ctx.api.sharedJobs.list()
        const sharedJobNames = await Promise.all(sharedJobPackages.map(async (packageName) => {
            const names = await ctx.api.sharedJobs.get(packageName).jobs.list()
            return names.map(jobName => ({packageName, jobName}))
        }))
        const revisions = await Promise.all(sharedJobNames.flat().map(async ({packageName, jobName}) => {
            const revisions = await ctx.api.sharedJobs.get(packageName).jobs.get(jobName).revisions.list()
            return revisions.map(revision => ({
                packageName,
                jobName,
                revision,
                implementationId: getSharedJobImplementationId(packageName, jobName, revision)
            }))
        }))
        ctx.log("Retrieved shared job list")
        ctx.sharedJobs = revisions.flat().reduce((acc, sharedJob) => {
            acc[sharedJob.implementationId] = sharedJob
            return acc
        }, {} as Record<number, SharedJobData>)
        const flatRevisions = revisions.flat()

        if (ctx.options.analyseSharedJobs) {
            ctx.log("Analysing shared jobs in depth")
            const {reportOne, complete} = ctx.startAction("Analysing shared jobs", revisions.length)
            let jobs: SingleSharedJobScanResult[] = []
            try {
                const results = await runPromises(ctx.options.sharedJobRunner, flatRevisions, (rev) => {
                    try {
                        return scan(ctx, rev)
                    } finally {
                        reportOne()
                    }
                })
                jobs = results.filter((result): result is SingleSharedJobScanResult => result !== undefined)
                const problemIndicators: OverallProblemIndicators = blankProblem()
                const componentCount: Record<number, number> = {}
                mergeProblemResults(jobs.map(x => x.jobs).flat(), {problemIndicators, componentCount})
                ctx.log("Finished analysing shared jobs in depth")
                return {
                    overall: {
                        problemIndicators,
                        componentCount
                    },
                    jobs,
                    list: flatRevisions
                }
            } catch (e) {
                ctx.logError("Failed to analyze shared jobs in detail.", e)
            } finally {
                complete()
            }

        }
        return {
            list: flatRevisions
        }
    } catch (e) {
        ctx.logError("Failed to retrieve shared jobs list. Continuing without shared job information.", e)
        return {
            list: []
        }
    }
}